// 运行时配置
import { getUserInfo } from '@/services/login';
import {
  FormOutlined,
  LogoutOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  QueryClient,
  QueryClientProvider,
} from '@tanstack/react-query';
import { history, RequestConfig } from '@umijs/max';
import {
  ConfigProvider,
  Dropdown,
  message,
  notification,
} from 'antd';
import dayjs from 'dayjs';
import timezone from 'dayjs/plugin/timezone';
import { EnumHttp } from './enum/http';
import zhCN from 'antd/locale/zh_CN';
import React from 'react';
import RechargePage from '@/components/Recharge';
import themeConfig from '@/config/theme';
import { ErrorBoundary } from "react-error-boundary";
// import * as Sentry from '@sentry/react';

dayjs.extend(timezone);

// Sentry.init({
//   enabled: process.env.NODE_ENV !== "development", // 开发环境下禁用 Sentry
//   dsn: SENTRY_DSN,
//   debug: false,
//   environment: process.env.NODE_ENV, // 根据环境切换
//
//   // 集成插件
//   integrations: [
//     Sentry.browserTracingIntegration(), // 堆栈跟踪
//     Sentry.replayIntegration(), // 开启 Session Replay 录制
//     Sentry.reportingObserverIntegration(), // 捕获浏览器的非标准错误报告
//   ],
//
//   // Session Replay 配置
//   replaysSessionSampleRate: 0.1, // 10% 的会话进行录制
//   replaysOnErrorSampleRate: 1.0,  // 100% 发生错误的会话进行录制
// });


export interface ResponseStructure {
  success: boolean;
  data: any;
  code?: number;
  message?: string;
}
const HTML_DOCTYPE_REGEXP = /^\s*<!doctype html/i;
const HTML_TAG_REGEXP = /^\s*<html[\s>]/i;

const isHtmlDocument = (value: unknown) => {
  if (typeof value !== 'string') {
    return false;
  }

  const normalized = value.trim();
  if (!normalized) {
    return false;
  }

  return (
    HTML_DOCTYPE_REGEXP.test(normalized) || HTML_TAG_REGEXP.test(normalized)
  );
};

const trimRequestPayload = (value: unknown): unknown => {
  if (typeof value === 'string') {
    console.log(value)
    return value.trim();
  }

  if (Array.isArray(value)) {
    return value.map(trimRequestPayload);
  }

  if (value instanceof FormData) {
    const formData = new FormData();
    value.forEach((entryValue, key) => {
      formData.append(
        key,
        typeof entryValue === 'string' ? entryValue.trim() : entryValue,
      );
    });
    return formData;
  }

  if (value instanceof URLSearchParams) {
    const searchParams = new URLSearchParams();
    value.forEach((entryValue, key) => {
      searchParams.append(key, entryValue.trim());
    });
    return searchParams;
  }

  if (Object.prototype.toString.call(value) === '[object Object]') {
    return Object.entries(value as Record<string, unknown>).reduce<
      Record<string, unknown>
    >((result, [key, entryValue]) => {
      result[key] = trimRequestPayload(entryValue);
      return result;
    }, {});
  }

  return value;
};

function fallbackRender({ error }) {
  return (
    <div role="alert" style={{ padding: "20px", background: "#fff2f0", border: "1px solid #ff4d4f", borderRadius: "5px", maxWidth: "600px", margin: "50px auto", textAlign: "center" }}>
      <h2 style={{ color: "#ff4d4f" }}>😢 哎呀，页面出错了！</h2>
      <p>可能的原因：</p>
      <ul style={{ textAlign: "left", color: "#333" }}>
        <li>网络连接异常，导致请求失败。</li>
        <li>服务器返回了无效的数据，导致解析错误。</li>
        <li>某些功能模块加载失败（例如 JavaScript 代码未正确执行）。</li>
        <li>应用内部逻辑出错，可能是未处理的异常。</li>
        <li>页面组件崩溃，可能是 React 渲染错误。</li>
        <li>API 请求超时，导致数据获取失败。</li>
        <li>浏览器兼容性问题，某些代码在当前环境下无法运行。</li>
        <li>用户输入了无效数据，导致未预料的错误。</li>
        <li>依赖的第三方服务（如 CDN、外部 API）出现故障。</li>
        <li>代码更新后未正确清除缓存，导致数据不匹配。</li>
      </ul>
      <pre style={{ color: "red", background: "#fff5f5", padding: "10px", borderRadius: "5px", overflowX: "auto" }}>
        {error.message}
      </pre>
      <button
        onClick={() => window.location.reload()}
        style={{ padding: "10px 20px", background: "#ff4d4f", color: "#fff", border: "none", borderRadius: "5px", cursor: "pointer", marginTop: "10px" }}
      >
        刷新页面
      </button>
    </div>
  );
}

export function rootContainer(container: any) {
  return (
    <ErrorBoundary fallbackRender={fallbackRender}>
      <QueryClientProvider client={new QueryClient()}>
        <ConfigProvider locale={zhCN} theme={themeConfig}>
          {container}
        </ConfigProvider>
      </QueryClientProvider>
    </ErrorBoundary>
  );
}

// 全局初始化数据配置，用于 Layout 用户信息和权限初始化
// 更多信息见文档：https://umijs.org/docs/api/runtime-config#getinitialstate
export async function getInitialState() {
  let userInfo = {};
  if (sessionStorage.getItem('userId')) {
    userInfo = await getUserInfo();
  }

  if (userInfo.data?.id) {
    return {
      user: userInfo.data,
    };
  } else {
    history.push('/login');
  }
}

export const layout = ({ initialState }) => {
  return {
    logo: 'https://img.alicdn.com/tfs/TB1YHEpwUT1gK0jSZFhXXaAtVXa-28-27.svg',
    menu: {
      locale: false,
    },
    collapsed: false,
    collapsedButtonRender: false,
    layout: 'mix',
    avatarProps: {
      title: sessionStorage.getItem('username'),
      render: (props: any, dom: any) => {
        return (
            <div style={{backgroundColor: '#fff', display: 'flex', alignItems: 'center', gap: '12px'}}>
              <RechargePage/>
              {
                  initialState?.user?.admin === 0 && <span style={{cursor: 'default'}}>
                套餐过期后，我们会向 {initialState?.user?.email} 发邮件通知您，请尽快续费，以免造成不必要的损失
              </span>
              }

              <span style={{cursor: "default"}}>当前时区：{Intl.DateTimeFormat().resolvedOptions().timeZone}</span>
              <span style={{color: '#ccc'}}>|</span>
              <Dropdown
                  menu={{
                    items: [
                      {
                        key: 'profile',
                        icon: <UserOutlined/>,
                        label: '个人信息',
                        onClick: () => {
                          // window.location.href = '/modifyPassword';
                          history.push('/profile');
                        },
                      },
                      {
                        key: 'reset',
                        icon: <FormOutlined/>,
                        label: '修改密码',
                        onClick: () => {
                          // window.location.href = '/modifyPassword';
                          history.push('/changePassword');
                        },
                      },
                      {
                        key: 'logout',
                        icon: <LogoutOutlined/>,
                        label: '退出登录',
                        onClick: () => {
                          window.location.href = '/login';
                          sessionStorage.clear();
                        },
                      },
                    ],
                  }}
              >
                {/*{dom}*/}
                <span>{sessionStorage.getItem('username')}</span>
              </Dropdown>
            </div>
        );
      },
    },
    // logout: async () => {
    //   history.push('/login');
    //   sessionStorage.clear();
    // },
  };
};

// 运行时配置
export const request: RequestConfig = {
  // 统一的请求设定
  // baseURL: `/client/${API_VERSION}`,
  timeout: 300000, // 请求300秒超时
  // 错误处理： umi@3 的错误处理方案。
  errorConfig: {
    // 错误抛出
    errorThrower: (res: ResponseStructure) => {
      const { success, data, code, message } = res;
      if (!success) {
        const error: any = new Error(message);
        error.name = 'BizError';
        error.info = { code, message:message, data };

        throw error; // 抛出自制的错误
      }
    },
    // 错误接收及处理
    errorHandler: (error: any, opts: any) => {
      if (opts?.skipErrorHandler) throw error;

      // 旧支付请求的错误不再干扰当前页面；登录失效仍按全局规则处理。
      const requiresLogin = [EnumHttp.NEED_LOGIN, EnumHttp.TOKEN_EXPIRE].includes(error?.info?.code) ||
        [401, 403].includes(error?.response?.status);
      if (!requiresLogin && typeof opts?.isPaymentRequestCurrent === 'function' &&
        !opts.isPaymentRequestCurrent()) return;

      // 支付轮询统一处理业务结果，避免全局和页面重复提示。
      if (!requiresLogin && opts?.handlePaymentErrorLocally && error.name === 'BizError' &&
        typeof error.info?.code === 'number') return;

      // 我们的 errorThrower 抛出的错误。
      if (error.name === 'BizError') {
        const errorInfo: ResponseStructure | undefined = error.info;
        if (errorInfo) {
          // message.error(errorInfo.message);
          const { code } = errorInfo;
          switch (code) {
            case EnumHttp.NEED_LOGIN:
              message.error({
                key: '请登录',
                type: 'error',
                content: errorInfo.message,
              });
              history.push('/login');
              sessionStorage.clear();
              break;
            case EnumHttp.TOKEN_EXPIRE:
              // TODO 这个提示暂时先删了
              // message.error({
              //   key: errorInfo.message,
              //   type: 'error',
              //   content: errorInfo.message,
              // });
              history.push('/login');
              sessionStorage.clear();
              break;
            case EnumHttp.WHMCS_MSG:
              break;
            default:
              message.error({
                key: errorInfo.message,
                type: 'error',
                content: errorInfo.message,
              });
          }
          return;
        }
      }

      if (error.response) {
        if ([401, 403].includes(error.response.status)) {
          sessionStorage.clear();
          history.push('/login');
        }
        const errorContent =
          error?.response?.data?.message || error?.response?.data;
        if (isHtmlDocument(errorContent)) {
          notification.open({
            title: `${error.request.responseURL}`,
            description: (
              <div
                className="max-h-[20vh] max-w-[20vw] overflow-auto rounded-lg border border-[#f0f0f0] bg-white"
                dangerouslySetInnerHTML={{ __html: errorContent }}
              />
            ),
          });
          return;
        }
        notification.open({
          title: `状态 ${error.response.status}`,
          description: `信息: ${error?.response?.data?.message || error?.response?.data || ''}`,
        });
        return;
      }

      if (error.request) {
        message.error('请求已发出，但没有收到响应，请重试');
        return;
      }

      message.error('请求错误，请重试');
    },
  },

  // 请求拦截器
  requestInterceptors: [
    (config: any) => {
      const token = sessionStorage.getItem('token');
      return {
        ...config,
        params: trimRequestPayload(config.params),
        data: trimRequestPayload(config.data),
        headers: {
          ...config.headers,
          deviceCode:sessionStorage.getItem('deviceCode'),
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          ...(token ? { token } : {}), // 只有在 token 存在时才添加到 headers
        },
      };
    },
  ],


  // 响应拦截器
  responseInterceptors: [
    (response) => {
      // 拦截响应数据，进行个性化处理
      const {
        data,
        status,
        config: { url = '' },
        headers,
      } = response;

      if (headers['token']) {
        sessionStorage.setItem('token', headers['token']);
      }

      // if (data.code !== 200 && data.code !== 2000) {
      //   return {
      //     ...response,
      //     data: {
      //       ...data,
      //       success: false,
      //     },
      //   };
      // }

      return response;
    },
  ],
};
